import { useState, useMemo } from 'react';
import { Link, useParams } from 'react-router-dom';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import Breadcrumb from '@/components/base/Breadcrumb';
import ScrollToTop from '@/components/base/ScrollToTop';
import SEOMeta from '@/components/base/SEOMeta';
import { categories } from '@/mocks/categories';
import { articles } from '@/mocks/articles';

const categoryImages: Record<string, string> = {
  hotel: 'https://readdy.ai/api/search-image?query=Luxury%20Macau%20hotel%20suite%20interior%20with%20elegant%20warm%20golden%20lighting%2C%20cream%20silk%20bedding%2C%20floor%20to%20ceiling%20windows%20with%20city%20view%2C%20asian%20luxury%20design%2C%20stylized%20editorial%20photography%2C%20soft%20ambient%20light&width=1200&height=600&seq=cat-header-hotel&orientation=landscape',
  dining: 'https://readdy.ai/api/search-image?query=Elegant%20fine%20dining%20restaurant%20interior%20with%20crystal%20chandeliers%20and%20warm%20golden%20lighting%2C%20white%20tablecloth%20setting%2C%20asian%20luxury%20aesthetic%2C%20stylized%20food%20photography%2C%20soft%20cream%20tones&width=1200&height=600&seq=cat-header-dining&orientation=landscape',
  entertainment: 'https://readdy.ai/api/search-image?query=Spectacular%20night%20water%20fountain%20show%20with%20colorful%20illuminated%20jets%2C%20grand%20luxury%20building%20backdrop%2C%20dramatic%20lighting%2C%20stylized%20artistic%20photography%2C%20reflection%20on%20water&width=1200&height=600&seq=cat-header-entertainment&orientation=landscape',
  shopping: 'https://readdy.ai/api/search-image?query=Luxury%20shopping%20mall%20interior%20with%20marble%20floors%20and%20golden%20details%2C%20high%20end%20boutiques%2C%20elegant%20warm%20lighting%2C%20asian%20luxury%20aesthetic%2C%20stylized%20editorial%20photography&width=1200&height=600&seq=cat-header-shopping&orientation=landscape',
  travel: 'https://readdy.ai/api/search-image?query=Historic%20Macau%20cityscape%20with%20Portuguese%20architecture%20at%20sunset%2C%20warm%20golden%20light%2C%20cobblestone%20streets%2C%20modern%20skyline%20background%2C%20stylized%20travel%20photography%2C%20soft%20atmospheric%20haze&width=1200&height=600&seq=cat-header-travel&orientation=landscape',
};

const articleCardImages: Record<string, string> = {
  'wynn-palace-suite-booking-guide': 'https://readdy.ai/api/search-image?query=Luxury%20Macau%20hotel%20suite%20bedroom%20with%20cream%20silk%20bedding%2C%20elegant%20warm%20golden%20lighting%2C%20floor%20to%20ceiling%20windows%2C%20asian%20luxury%20interior%20design%2C%20stylized%20editorial%20photography&width=800&height=500&seq=article-hotel-01&orientation=landscape',
  'wynn-palace-michelin-restaurants': 'https://readdy.ai/api/search-image?query=Michelin%20star%20restaurant%20exquisite%20dish%20presentation%20with%20golden%20chandelier%20background%2C%20fine%20dining%20atmosphere%2C%20warm%20elegant%20lighting%2C%20asian%20luxury%20aesthetic%2C%20stylized%20food%20photography&width=800&height=500&seq=article-dining-01&orientation=landscape',
  'wynn-palace-performance-lake-show': 'https://readdy.ai/api/search-image?query=Magnificent%20night%20water%20fountain%20show%20with%20colorful%20illuminated%20jets%20and%20fire%20effects%2C%20grand%20luxury%20building%20backdrop%2C%20dramatic%20lighting%2C%20stylized%20cinematic%20photography&width=800&height=500&seq=article-entertainment-01&orientation=landscape',
  'wynn-palace-luxury-shopping': 'https://readdy.ai/api/search-image?query=Luxury%20boutique%20storefront%20with%20golden%20warm%20lighting%2C%20marble%20floor%20reflections%2C%20high%20end%20fashion%20display%2C%20elegant%20mall%20interior%2C%20asian%20luxury%20aesthetic&width=800&height=500&seq=article-shopping-01&orientation=landscape',
  'macau-three-day-itinerary': 'https://readdy.ai/api/search-image?query=Historic%20Macau%20streets%20with%20Portuguese%20architecture%20and%20warm%20sunset%20light%2C%20colorful%20colonial%20buildings%2C%20cobblestone%20path%2C%20stylized%20travel%20photography%2C%20soft%20atmospheric%20haze&width=800&height=500&seq=article-travel-01&orientation=landscape',
  'wynn-palace-spa-experience': 'https://readdy.ai/api/search-image?query=Luxury%20spa%20treatment%20room%20with%20soft%20warm%20candlelight%2C%20cream%20towels%20and%20orchid%20flowers%2C%20serene%20atmosphere%2C%20asian%20wellness%20aesthetic%2C%20stylized%20editorial%20photography&width=800&height=500&seq=article-spa-01&orientation=landscape',
  'wynn-palace-family-friendly': 'https://readdy.ai/api/search-image?query=Family%20friendly%20luxury%20hotel%20pool%20area%20with%20children%20water%20slides%2C%20warm%20sunny%20day%2C%20tropical%20landscape%2C%20asian%20luxury%20resort%2C%20stylized%20editorial%20photography&width=800&height=500&seq=article-family-01&orientation=landscape',
  'wynn-palace-wedding-venue': 'https://readdy.ai/api/search-image?query=Elegant%20luxury%20wedding%20ballroom%20setup%20with%20golden%20decorations%20and%20cream%20flowers%2C%20crystal%20chandeliers%2C%20romantic%20warm%20lighting%2C%20asian%20luxury%20aesthetic&width=800&height=500&seq=article-wedding-01&orientation=landscape',
};

export default function CategoryPage() {
  const { slug } = useParams<{ slug: string }>();

  const category = useMemo(() => categories.find((c) => c.slug === slug), [slug]);
  const filteredArticles = useMemo(
    () => (slug ? articles.filter((a) => a.category === slug) : []),
    [slug]
  );

  const [sortBy, setSortBy] = useState<'latest' | 'popular'>('latest');

  const sortedArticles = useMemo(() => {
    if (sortBy === 'latest') {
      return [...filteredArticles].sort(
        (a, b) => new Date(b.publishDate).getTime() - new Date(a.publishDate).getTime()
      );
    }
    return filteredArticles;
  }, [filteredArticles, sortBy]);

  if (!category) {
    return (
      <>
        <Navbar />
        <div className="min-h-screen pt-20 flex items-center justify-center">
          <div className="text-center">
            <h1 className="font-heading text-2xl text-foreground-900 mb-3">分类未找到</h1>
            <Link to="/" className="text-primary-600 hover:text-primary-700 text-sm">返回首页</Link>
          </div>
        </div>
        <Footer />
      </>
    );
  }

  const catMetaDesc = `永利皇宫${category.name}专题，${category.description}。浏览最新攻略、体验分享与实用贴士。`;
  const catMetaKeywords = `永利皇宫${category.name},澳门${category.name},永利皇宫攻略`;

  return (
    <>
      <ScrollToTop />
      <SEOMeta
        title={`${category.name} - 永利皇宫`}
        description={catMetaDesc}
        keywords={catMetaKeywords}
        canonicalUrl={`https://embedded-expo.cn/category/${slug}`}
      />

      <Navbar />

      <main>
        <section className="relative w-full h-[320px] md:h-[400px] flex items-end overflow-hidden">
          <img
            src={categoryImages[slug] || categoryImages.hotel}
            alt={`永利皇宫${category.name}`}
            className="absolute inset-0 w-full h-full object-cover object-top"
          />
          <div className="absolute inset-0 bg-gradient-to-t from-black/60 via-black/20 to-transparent"></div>
          <div className="relative z-10 w-full px-4 md:px-6 pb-8 md:pb-10">
            <div className="max-w-6xl mx-auto">
              <Breadcrumb
                items={[
                  { label: '首页', to: '/' },
                  { label: category.name },
                ]}
              />
            </div>
          </div>
        </section>

        <section className="w-full px-4 md:px-6 py-10 md:py-14 bg-background-50">
          <div className="max-w-6xl mx-auto">
            <div className="mb-8">
              <div className="flex items-center gap-3 mb-3">
                <div className="w-10 h-10 flex items-center justify-center rounded-xl bg-primary-100 text-primary-600">
                  <i className={`${category.icon} text-lg`}></i>
                </div>
                <h1 className="font-heading text-2xl md:text-3xl font-semibold text-foreground-900">
                  {category.name}
                </h1>
              </div>
              <p className="text-foreground-600 text-sm">{category.description}</p>
            </div>

            <div className="flex items-center gap-3 mb-8 overflow-x-auto pb-2">
              {categories.map((cat) => (
                <Link
                  key={cat.slug}
                  to={`/category/${cat.slug}`}
                  className={`px-4 py-2 rounded-full text-sm font-medium whitespace-nowrap transition-all duration-200 ${
                    cat.slug === slug
                      ? 'bg-primary-500 text-background-50'
                      : 'bg-secondary-100 text-secondary-700 hover:bg-secondary-200'
                  }`}
                >
                  {cat.name}
                </Link>
              ))}
            </div>

            <div className="flex items-center justify-end gap-2 mb-6">
              <span className="text-foreground-500 text-xs">排序：</span>
              <button
                className={`px-3 py-1.5 rounded-lg text-xs font-medium transition-colors ${
                  sortBy === 'latest'
                    ? 'bg-primary-100 text-primary-700'
                    : 'bg-secondary-100 text-secondary-600 hover:bg-secondary-200'
                }`}
                onClick={() => setSortBy('latest')}
              >
                最新
              </button>
              <button
                className={`px-3 py-1.5 rounded-lg text-xs font-medium transition-colors ${
                  sortBy === 'popular'
                    ? 'bg-primary-100 text-primary-700'
                    : 'bg-secondary-100 text-secondary-600 hover:bg-secondary-200'
                }`}
                onClick={() => setSortBy('popular')}
              >
                推荐
              </button>
            </div>

            {sortedArticles.length === 0 ? (
              <div className="text-center py-20">
                <p className="text-foreground-500 text-sm">该分类下暂无文章</p>
              </div>
            ) : (
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5 md:gap-6">
                {sortedArticles.map((article) => (
                  <Link
                    key={article.slug}
                    to={`/article/${article.slug}`}
                    className="group rounded-xl bg-background-100 border border-secondary-200/60 overflow-hidden hover:border-primary-300/50 transition-all duration-300 cursor-pointer"
                  >
                    <div className="aspect-[16/10] overflow-hidden">
                      <img
                        src={articleCardImages[article.slug] || articleCardImages['wynn-palace-suite-booking-guide']}
                        alt={article.title}
                        className="w-full h-full object-cover object-top transition-transform duration-500 group-hover:scale-105"
                      />
                    </div>
                    <div className="p-5">
                      <div className="flex items-center gap-2 mb-3">
                        <span className="text-foreground-400 text-xs">{article.readTime}</span>
                        <span className="text-foreground-300 text-xs">·</span>
                        <span className="text-foreground-400 text-xs">{article.publishDate}</span>
                      </div>
                      <h3 className="font-heading text-sm font-semibold text-foreground-900 mb-2 line-clamp-2 group-hover:text-primary-600 transition-colors">
                        {article.title}
                      </h3>
                      <p className="text-foreground-600 text-xs leading-relaxed line-clamp-2">
                        {article.excerpt}
                      </p>
                      <div className="flex flex-wrap gap-1.5 mt-3">
                        {article.tags.slice(0, 2).map((tag) => (
                          <span
                            key={tag}
                            className="px-2 py-0.5 rounded-md bg-secondary-100 text-secondary-600 text-xs"
                          >
                            {tag}
                          </span>
                        ))}
                      </div>
                    </div>
                  </Link>
                ))}
              </div>
            )}
          </div>
        </section>
      </main>

      <Footer />
    </>
  );
}